import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Check, XCircle } from "lucide-react";
import { useAuth } from "@/features/auth/context/AuthContext";
import { acceptInvite } from "@/features/team/teamApi";
import { Logo } from "@/shared/brand";

/** Accept a team invite (authed user). The email-match auto-accept on signup/login
 *  is the primary path; this handles a logged-in teammate clicking the link. */
export default function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const { refreshWorkspaces, setActiveWorkspace } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<{ ok: boolean; msg: string } | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current || !token) return;
    ran.current = true;
    (async () => {
      try {
        const res = await acceptInvite(token);
        await refreshWorkspaces();
        if (res.workspaceId) setActiveWorkspace(res.workspaceId);
        setState({ ok: true, msg: `You've joined ${res.workspaceName ?? "the workspace"}.` });
        setTimeout(() => navigate("/vault"), 1100);
      } catch (e) {
        setState({ ok: false, msg: e instanceof Error ? e.message : "This invite is no longer valid." });
      }
    })();
  }, [token, refreshWorkspaces, setActiveWorkspace, navigate]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-canvas px-4 text-center">
      <Logo size="sm" showTagline tagline="Vault" />
      <div className="mt-8 rounded-2xl border border-ink-100 bg-white px-8 py-10 shadow-card">
        {!state ? (
          <Loader2 className="mx-auto animate-spin text-ink-400" />
        ) : state.ok ? (
          <>
            <Check className="mx-auto text-emerald" size={28} />
            <p className="mt-3 text-[14px] font-medium text-ink-800">{state.msg}</p>
            <p className="mt-1 text-[12.5px] text-ink-400">Taking you in…</p>
          </>
        ) : (
          <>
            <XCircle className="mx-auto text-rose" size={28} />
            <p className="mt-3 text-[14px] font-medium text-ink-800">{state.msg}</p>
            <button onClick={() => navigate("/vault")} className="mt-4 text-[13px] font-medium text-gold-600 hover:text-gold-700">Go to Vault</button>
          </>
        )}
      </div>
    </div>
  );
}
